import { useMemo } from 'react';

import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { useSearchParams } from 'react-router';

import type { ParsedEngagementRecord } from '../useParseRecords';

export interface ClientSideFilters {
  clientNumber?: string;
  clientName?: string;
  searchText?: string;
  executedFilter?: 'yes' | 'no';
  recentFilter?: boolean;
  openClosedFilter?: 'open' | 'closed';
  // 'updated' quick preset — filters by last-modified date.
  updatedFrom?: string;
  updatedTo?: string;
  // Advanced filters — independent ranges so open and closed dates don't clobber each other.
  openDateFrom?: string;
  openDateTo?: string;
  closedDateFrom?: string;
  closedDateTo?: string;
  logicOperator: 'AND' | 'OR';
}

// Advanced open/closed date ranges are independent URL param pairs so setting
// one never clobbers the other.
function applyAdvancedDateParams(urlFilters: URLSearchParams, filters: ClientSideFilters): void {
  const openDateFrom = urlFilters.get('openDateFrom');
  if (openDateFrom) filters.openDateFrom = openDateFrom;

  const openDateTo = urlFilters.get('openDateTo');
  if (openDateTo) filters.openDateTo = openDateTo;

  const closedDateFrom = urlFilters.get('closedDateFrom');
  if (closedDateFrom) filters.closedDateFrom = closedDateFrom;

  const closedDateTo = urlFilters.get('closedDateTo');
  if (closedDateTo) filters.closedDateTo = closedDateTo;
}

function useBuildGraphQLVariables() {
  const [urlFilters] = useSearchParams();

  const clientSideFilters = useMemo((): ClientSideFilters => {
    const logicOperator = (urlFilters.get('logicOperator') as 'AND' | 'OR') || 'AND';
    const filters: ClientSideFilters = { logicOperator };

    const clientNumber = urlFilters.get('clientNumber');
    if (clientNumber) filters.clientNumber = clientNumber;

    const clientName = urlFilters.get('clientName');
    if (clientName) filters.clientName = clientName;

    const searchText = urlFilters.get('q');
    if (searchText) filters.searchText = searchText;

    const executedFilter = urlFilters.get('executedFilter');
    if (executedFilter === 'yes' || executedFilter === 'no') {
      filters.executedFilter = executedFilter;
    }

    const quickDatePreset = urlFilters.get('quickDatePreset');
    if (quickDatePreset === 'recent') {
      filters.recentFilter = true;
    } else if (quickDatePreset === 'updated') {
      filters.updatedFrom = dayjs().subtract(30, 'day').format('YYYY-MM-DD');
      filters.updatedTo = dayjs().format('YYYY-MM-DD');
    } else if (quickDatePreset === 'open' || quickDatePreset === 'closed') {
      filters.openClosedFilter = quickDatePreset;
    } else {
      applyAdvancedDateParams(urlFilters, filters);
    }

    return filters;
  }, [urlFilters]);

  return { clientSideFilters };
}

function checkRecentFilter(record: ParsedEngagementRecord): boolean {
  // Record must have a clm_engagement_record with a created_at date in the last 30 days
  if (!record.createdAt) return false;
  const thirtyDaysAgo = dayjs().subtract(30, 'day').startOf('day');
  return record.createdAt.isAfter(thirtyDaysAgo);
}

function checkDateRange(
  dateField: Dayjs | null | undefined,
  from: string | undefined,
  to: string | undefined,
): boolean | null {
  if (!from && !to) return null;
  if (!dateField) return false;
  if (from && dateField.isBefore(dayjs(from))) return false;
  if (to && dateField.isAfter(dayjs(to).endOf('day'))) return false;
  return true;
}

export function applyClientSideFilters(
  records: ParsedEngagementRecord[],
  filters: ClientSideFilters,
): ParsedEngagementRecord[] {
  const useOr = filters.logicOperator === 'OR';

  return records.filter((record) => {
    const checks: boolean[] = [];

    // Client number filter check
    if (filters.clientNumber) {
      checks.push((record.clientNumber ?? '').toLowerCase().includes(filters.clientNumber.toLowerCase()));
    }

    // Client name filter check
    if (filters.clientName) {
      checks.push((record.clientName ?? '').toLowerCase().includes(filters.clientName.toLowerCase()));
    }

    // Free-text search check (client number or client name)
    if (filters.searchText) {
      const term = filters.searchText.toLowerCase();
      checks.push(
        (record.clientNumber ?? '').toLowerCase().includes(term) ||
          (record.clientName ?? '').toLowerCase().includes(term),
      );
    }

    // Executed filter check
    if (filters.executedFilter === 'yes') {
      checks.push(record.isExecuted);
    } else if (filters.executedFilter === 'no') {
      checks.push(!record.isExecuted);
    }

    // Recent filter — clm_engagement_record.created_at within last 30 days
    if (filters.recentFilter) {
      checks.push(checkRecentFilter(record));
    }

    // Open / Closed filter — based on whether the client has a closed date
    if (filters.openClosedFilter === 'open') {
      checks.push(!record.clientClosedDate);
    } else if (filters.openClosedFilter === 'closed') {
      checks.push(Boolean(record.clientClosedDate));
    }

    // Date filter checks — updated (quick preset), open date and closed date are
    // independent ranges so setting one never clobbers the others.
    const updatedResult = checkDateRange(record.updatedAt ?? record.createdAt, filters.updatedFrom, filters.updatedTo);
    if (updatedResult !== null) {
      checks.push(updatedResult);
    }

    const openDateResult = checkDateRange(record.clientOpenDate, filters.openDateFrom, filters.openDateTo);
    if (openDateResult !== null) {
      checks.push(openDateResult);
    }

    const closedDateResult = checkDateRange(record.clientClosedDate, filters.closedDateFrom, filters.closedDateTo);
    if (closedDateResult !== null) {
      checks.push(closedDateResult);
    }

    // If no filters, include all
    if (checks.length === 0) return true;

    // Apply logic operator
    return useOr ? checks.some(Boolean) : checks.every(Boolean);
  });
}

export default useBuildGraphQLVariables;
